import { ConceptCategory, Difficulty, LearningMode } from '../types';
import {
  PredefinedTopic,
  DifficultySettings,
  AlgorithmicComplexity,
  UnityApiKnowledge,
  CodeScope,
  CognitiveLoad,
  DifficultyLabels
} from '../types';

// === Labels et descriptions des concepts ===
export const CONCEPT_LABELS: Record<ConceptCategory, string> = {
  // Fondamentaux C#
  [ConceptCategory.DATA_TYPES]: 'Types de données (int, string, bool, float, Vector3, arrays, List<T>)',
  [ConceptCategory.VARIABLES]: 'Déclaration, initialisation et assignation de variables',
  [ConceptCategory.ACCESS_MODIFIERS]: 'Modificateurs d\'accès (public, private, [SerializeField])',
  [ConceptCategory.METHODS]: 'Signatures de méthodes (void, string, IEnumerator, paramètres)',
  [ConceptCategory.OPERATORS]: 'Opérateurs (arithmétiques, comparaison, logiques, incrémentation)',
  [ConceptCategory.CONDITIONALS]: 'Instructions conditionnelles (if, else if, else)',
  [ConceptCategory.LOOPS]: 'Boucles (for, foreach)',
  [ConceptCategory.ENUMS]: 'Énumérations et instructions switch',
  [ConceptCategory.COMMENTS]: 'Commentaires et documentation',
  [ConceptCategory.ERROR_IDENTIFICATION]: 'Identification d\'erreurs courantes',
  [ConceptCategory.NAMING_CONVENTIONS]: 'Conventions de nommage (PascalCase)',
  [ConceptCategory.ABSTRACT_CLASSES]: 'Classes abstraites (abstract class, méthodes abstract, override)',
  [ConceptCategory.INTERFACES]: 'Interfaces C# (interface, implémentation par une classe)',
  
  // API Unity & Scripting
  [ConceptCategory.MONOBEHAVIOUR_LIFECYCLE]: 'Cycle de vie MonoBehaviour (Awake, Start, Update, etc.)',
  [ConceptCategory.INPUT_HANDLING]: 'Gestion des entrées (Input.GetKey, Input.GetMouseButton, etc.)',
  [ConceptCategory.GAMEOBJECT_MANIPULATION]: 'Manipulation GameObject (tags, Instantiate, Destroy)',
  [ConceptCategory.COMPONENT_ACCESS]: 'Accès aux composants (GetComponent<T>)',
  [ConceptCategory.PHYSICS_RIGIDBODY]: 'Physique 2D (Rigidbody2D) et détection collision/trigger',
  [ConceptCategory.VECTOR_MATH]: 'Mathématiques vectorielles (Vector3, Random.Range, Time.deltaTime)',
  [ConceptCategory.COROUTINES]: 'Coroutines (IEnumerator, StartCoroutine, yield)',
  [ConceptCategory.ANIMATION_SYSTEM]: 'Système d\'animation (Animator interactions)',
  [ConceptCategory.UI_INTERACTION]: 'Interaction UI de base (TextMeshProUGUI, boutons)',
  [ConceptCategory.DEBUGGING]: 'Débogage (Debug.Log)',

  // Python/JS Fundamentals
  [ConceptCategory.PYJS_PRINT_OUTPUT]: 'Affichage (print / console.log)',
  [ConceptCategory.PYJS_USER_INPUT]: 'Saisie utilisateur (input / prompt)',
  [ConceptCategory.PYJS_STRINGS]: 'Manipulation de chaînes de caractères',
  [ConceptCategory.PYJS_LISTS_ARRAYS]: 'Listes et tableaux',
  [ConceptCategory.PYJS_DICTIONARIES]: 'Dictionnaires / Objets',
  [ConceptCategory.PYJS_TYPE_CONVERSION]: 'Conversion de types',

  // Godot GDScript
  [ConceptCategory.GODOT_PRINT_OUTPUT]: 'Affichage console Godot (print)',
  [ConceptCategory.GODOT_ARRAYS_DICTS]: 'Tableaux (Array) et Dictionnaires GDScript',
  [ConceptCategory.GODOT_NODES_SCENES]: 'Nodes et Scènes (get_node, $, @onready, @export)',
  [ConceptCategory.GODOT_LIFECYCLE]: 'Cycle de vie Godot (_ready, _process, delta)',
  [ConceptCategory.GODOT_SIGNALS]: 'Signaux (signal, emit, connect)',
  [ConceptCategory.GODOT_AWAIT_CALLABLES]: 'Await, Callable et callbacks',
  [ConceptCategory.GODOT_TYPED_GDSCRIPT]: 'GDScript typé et mots-clés avancés (class_name, match, enum)',
  [ConceptCategory.GODOT_VECTOR_MATH]: 'Maths vectorielles (Vector2, normalized, lerp)',
  [ConceptCategory.GODOT_SCENE_INSTANCING]: 'Instanciation de scènes (preload, instantiate, add_child)',
  [ConceptCategory.GODOT_CHARACTER_MOVEMENT]: 'Mouvement de personnage (CharacterBody2D, velocity, move_and_slide)',
  [ConceptCategory.GODOT_OOP_PATTERNS]: 'POO et patterns GDScript (héritage, set/get, Resources, state machines)'
};

// === Labels de difficulté ===
export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  [Difficulty.BEGINNER]: 'Débutant',
  [Difficulty.INTERMEDIATE]: 'Intermédiaire',
  [Difficulty.ADVANCED]: 'Avancé'
};

// === Groupes de concepts par catégorie ===
export const CONCEPT_GROUPS = {
  'Fondamentaux C#': [
    ConceptCategory.DATA_TYPES,
    ConceptCategory.VARIABLES,
    ConceptCategory.ACCESS_MODIFIERS,
    ConceptCategory.METHODS,
    ConceptCategory.OPERATORS,
    ConceptCategory.CONDITIONALS,
    ConceptCategory.LOOPS,
    ConceptCategory.ENUMS,
    ConceptCategory.COMMENTS,
    ConceptCategory.ERROR_IDENTIFICATION,
    ConceptCategory.NAMING_CONVENTIONS
  ],
  'API Unity & Scripting': [
    ConceptCategory.MONOBEHAVIOUR_LIFECYCLE,
    ConceptCategory.INPUT_HANDLING,
    ConceptCategory.GAMEOBJECT_MANIPULATION,
    ConceptCategory.COMPONENT_ACCESS,
    ConceptCategory.PHYSICS_RIGIDBODY,
    ConceptCategory.VECTOR_MATH,
    ConceptCategory.COROUTINES,
    ConceptCategory.ANIMATION_SYSTEM,
    ConceptCategory.UI_INTERACTION,
    ConceptCategory.DEBUGGING
  ]
} as const;

// === Configuration par défaut ===
export const DEFAULT_CONFIG = {
  EXERCISE_TIME_LIMIT_MINUTES: 10,
  MIN_EXERCISE_TIME_MINUTES: 3,
  MAX_EXERCISE_TIME_MINUTES: 7,
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 50,
  SESSION_DURATION_HOURS: 24,
  PROGRESS_UPDATE_THRESHOLD: 0.1, // 10% de changement pour mettre à jour
  MIN_ATTEMPTS_FOR_MASTERY: 3,
  MASTERY_THRESHOLD: 80, // 80% de réussite pour considérer un concept maîtrisé
} as const;

// === Messages de feedback ===
export const FEEDBACK_MESSAGES = {
  COMPILATION_SUCCESS: 'Votre code compilerait sans erreur dans Unity ! 🎉',
  COMPILATION_ERROR: 'Votre code contient des erreurs de compilation qui l\'empêcheraient de fonctionner dans Unity.',
  LOGIC_EXCELLENT: 'Excellente logique ! Votre approche est correcte et bien structurée.',
  LOGIC_GOOD: 'Bonne logique globale, avec quelques points d\'amélioration possibles.',
  LOGIC_NEEDS_WORK: 'La logique nécessite quelques ajustements pour fonctionner correctement.',
  PRACTICES_GOOD: 'Vous suivez de bonnes pratiques Unity/C# !',
  PRACTICES_IMPROVABLE: 'Quelques bonnes pratiques pourraient être appliquées.',
  IMPROVEMENT_AVAILABLE: 'Votre solution fonctionne, mais pourrait être optimisée.',
} as const;

// === Patterns pour l'analyse de code ===
export const CODE_PATTERNS = {
  // Patterns Unity courants
  UNITY_METHODS: ['Awake', 'Start', 'Update', 'FixedUpdate', 'LateUpdate', 'OnTriggerEnter', 'OnCollisionEnter2D'],
  INPUT_METHODS: ['Input.GetKey', 'Input.GetKeyDown', 'Input.GetButton', 'Input.GetMouseButton'],
  UNITY_TYPES: ['GameObject', 'Transform', 'Rigidbody', 'Rigidbody2D', 'Vector3', 'Vector2', 'Quaternion'],
  COROUTINE_KEYWORDS: ['IEnumerator', 'StartCoroutine', 'yield return', 'WaitForSeconds'],
  
  // Patterns de bonnes pratiques
  SERIALIZE_FIELD: '[SerializeField]',
  TIME_DELTA: 'Time.deltaTime',
  DEBUG_LOG: 'Debug.Log',
  
  // Patterns d'erreurs courantes
  MISSING_SEMICOLON: /[^;]\s*$/m,
  WRONG_CASE: /[a-z][A-Za-z]*\(/,
  MISSING_NAMESPACE: /^(?!.*using\s+UnityEngine)/m,
} as const;

// === Prompts système pour Gemini ===
export const SYSTEM_PROMPTS = {
  EXERCISE_GENERATOR: `Tu es un expert en programmation Unity/C# spécialisé dans la création d'exercices pédagogiques pour débutants.

Ton rôle est de créer des exercices courts (3-7 minutes) ciblés sur les concepts fondamentaux de Unity et C#.

Contexte pédagogique :
- Étudiants débutants en programmation Unity/C#
- Préparation à la certification "Unity Certified User Programmer"
- Approche constructiviste avec échafaudage progressif

Contraintes :
- Exercices courts et ciblés
- Code simple et compréhensible
- Contexte Unity clairement décrit si nécessaire
- Solutions réalistes et applicables

Types d'exercices :
1. COMPLETE_CODE : Compléter un script en ajoutant 2-5 lignes de code
2. FIX_ERRORS : Corriger 1-3 erreurs dans un script fourni

Réponds UNIQUEMENT au format JSON demandé.`,

  FEEDBACK_GENERATOR: `Tu es un expert en programmation Unity/C# spécialisé dans l'évaluation pédagogique.

Ton rôle est d'analyser les solutions d'étudiants débutants et de fournir un feedback constructif et détaillé.

Critères d'évaluation :
1. Logique de programmation (correctness, approche)
2. Compilation (erreurs de syntaxe, types, etc.)
3. Bonnes pratiques Unity/C# (conventions, performance, lisibilité)
4. Suggestions d'amélioration (optimisations, alternatives)

Approche pédagogique :
- Feedback positif et encourageant
- Explications claires et accessibles
- Suggestions concrètes d'amélioration
- Focus sur l'apprentissage progressif

Réponds UNIQUEMENT au format JSON demandé.`
} as const;

// === Templates de prompts dynamiques ===
export const PROMPT_TEMPLATES = {
  GENERATE_EXERCISE: `Génère un exercice de programmation Unity/C# avec les paramètres suivants :
- Concepts ciblés : {concepts}
- Difficulté : {difficulty}
- Type : {type}
- Durée estimée : {timeMinutes} minutes
- Charge cognitive : {cognitiveLoad}

{customRequirements}

{cognitiveLoadInstructions}

CONTRAINTES IMPORTANTES SELON LES PARAMÈTRES DE DIFFICULTÉ:
Ces contraintes sont OBLIGATOIRES et définissent la complexité exacte de l'exercice.
Respecte scrupuleusement les niveaux demandés - un exercice "Une Ligne" ne peut pas devenir un script complet.

MARKDOWN ET FORMATAGE:
Dans les champs "title" et "description" du JSON, utilise la syntaxe Markdown (ex: **mot-clé**) 
pour mettre en évidence les termes importants Unity/C#.

IMPORTANT: Sépare complètement les instructions du code. Le code de départ sera directement inséré dans l'éditeur, donc les instructions ne doivent PAS contenir de blocs de code.

Réponds au format JSON avec les champs suivants :
{
  "title": "Titre court et descriptif (avec **Markdown** si pertinent)",
  "description": "Description de l'objectif pédagogique (avec **Markdown** si pertinent)", 
  "prompt": "Instructions détaillées SANS code (utilise **gras** pour les concepts clés, 'code' pour les noms de méthodes/variables, mais PAS de blocs de code)",
  "context": "Description de la structure Unity (GameObjects, composants, etc.)",
  "startingCode": "Code de départ avec commentaires TODO indiquant où l'étudiant doit compléter",
  "expectedSolution": "Solution complète et fonctionnelle",
  "hints": ["Indice pratique 1", "Indice pratique 2"],
  "learningOutcomes": ["Objectif d'apprentissage 1", "Objectif d'apprentissage 2"],
  "commonMistakes": [
    {
      "mistake": "Description de l'erreur courante",
      "explanation": "Pourquoi cette erreur arrive",
      "example": "Exemple de code incorrect (optionnel)"
    }
  ],
  "successCriteria": ["Critère de réussite 1", "Critère de réussite 2"], 
  "estimatedTimeMinutes": nombre
}

FORMATAGE pour "prompt" (SANS CODE):
- Utilise **gras** pour les concepts Unity/C# (ex: **GameObject**, **MonoBehaviour**)
- Utilise 'code' pour les noms de méthodes/variables (ex: 'Update()', 'transform.position')
- Utilise "guillemets" pour les valeurs (ex: "Player", "Idle")
- Structure en étapes numérotées
- AUCUN bloc de code - le code va dans "startingCode"

FORMATAGE pour "startingCode" - IMPÉRATIF:
- Code C# complet avec TOUS les using statements nécessaires (minimum: using UnityEngine;)
- Classe complète qui hérite de MonoBehaviour
- Structure complète avec méthodes Start(), Update() si pertinentes
- Commentaires // TODO: indiquant précisément où l'étudiant doit compléter
- Le code doit être prêt à être copié-collé dans Unity et fonctionner

FORMATAGE pour "expectedSolution" - IMPÉRATIF:
- Code C# complet et fonctionnel avec TOUS les using statements
- Même structure que startingCode mais avec les TODO complétés
- Le code doit compiler et fonctionner parfaitement dans Unity
- Inclure tous les using, la classe complète, toutes les méthodes nécessaires`,

  EVALUATE_SOLUTION: `Tu es un mentor expérimenté en programmation Unity/C#. Ton rôle est de fournir un feedback pédagogique constructif qui aide l'étudiant à progresser.

ÉNONCÉ DONNÉ À L'ÉTUDIANT (c'est le contrat) :
{exercisePrompt}

CODE DE DÉPART (template fourni) :
{originalCode}

SOLUTION SOUMISE PAR L'ÉTUDIANT :
{studentSolution}

UNE SOLUTION DE RÉFÉRENCE (une réponse juste parmi d'autres, pas la norme) :
{expectedSolution}

MISSION PÉDAGOGIQUE:
Analyse la solution de l'étudiant avec bienveillance mais précision. Concentre-toi sur l'apprentissage : que comprend-il bien ? Que doit-il améliorer ? Comment l'aider à progresser ?

STRUCTURE DE RÉPONSE OBLIGATOIRE (JSON) :
{
  "logicAnalysis": "Analyse pédagogique de l'approche de l'étudiant",
  "whatWorked": ["Ce que l'étudiant a réussi 1", "Réussite 2"],
  "keyIssues": ["Problème principal à corriger", "Enjeu important"],
  "compilationStatus": {
    "wouldCompile": true_ou_false,
    "errors": ["Erreur de compilation 1"],
    "warnings": ["Avertissement 1"]
  },
  "learningInsights": ["Concept à renforcer 1", "Point d'apprentissage 2"],
  "nextSteps": ["Prochaine étape concrete 1", "Action à prendre 2"],
  "overallScore": nombre_entre_0_et_100
}

INSTRUCTIONS DÉTAILLÉES:

📊 **logicAnalysis** : Analyse pédagogique en 2-3 phrases
- Commence par ce qui fonctionne bien
- Identifie le niveau de compréhension de l'étudiant
- Explique ce qui manque par rapport à ce que l'énoncé demande

✅ **whatWorked** : Points positifs spécifiques (2-4 éléments max)
- Reconnaître les bonnes décisions
- Valoriser les concepts bien utilisés  
- Mentionner les bonnes pratiques appliquées

⚠️ **keyIssues** : Problèmes principaux à résoudre (1-3 max)
- Erreurs logiques majeures
- Concepts mal compris
- Approches incorrectes
- PAS de détails techniques - juste l'essentiel

🔧 **compilationStatus** : État technique du code
- wouldCompile: true si compilable en C#
- errors: Erreurs de syntaxe empêchant la compilation
- warnings: uniquement les vrais avertissements du compilateur C# (un conseil de style va dans nextSteps)
- Préfixe chaque erreur/avertissement par "Ligne N: " quand une ligne précise du code soumis est identifiable (ex: "Ligne 12: point-virgule manquant") ; sinon, pas de préfixe

💡 **learningInsights** : Concepts à approfondir (2-4 max)
- Quels concepts Unity/C# réviser ?
- Quelles notions semblent floues ?
- Suggestions de révision ciblées

🎯 **nextSteps** : Actions concrètes pour progresser (2-3 max)
- Étapes précises pour corriger
- Ressources à consulter
- Exercices complémentaires suggérés

🏆 **overallScore** : Note calculée selon la grille suivante

=== GRILLE DE NOTATION (BASE 100 POINTS) ===

**Compilation (30 points) :**
- Code compile parfaitement : 30 pts
- Erreur(s) syntaxique(s) mineure(s) (point-virgule, typo) : 20 pts
- Erreur(s) de type ou syntaxe complexe : 10 pts
- Code ne compile pas du tout : 0 pts

**Logique Fonctionnelle (40 points) :**
- Solution parfaitement fonctionnelle : 40 pts
- Fonctionne avec erreur mineure (ex: ordre sub-optimal) : 30 pts
- Partiellement fonctionnelle : 20 pts
- Logique fondamentalement incorrecte : 10 pts
- Aucune logique valide : 0 pts

**Points de Connaissance (20 points) :**
- Tous les points validés : 20 pts
- Majorité validée : 15 pts
- Minorité validée : 10 pts
- Aucun validé : 0 pts

**Bonnes Pratiques (10 points) :**
- Conventions + structure + lisibilité : 10 pts
- Partiel : 5 pts
- Aucune : 0 pts

**SEUILS DE CATÉGORIE :**
- 90-100 : "Excellent travail !" (✓ Réussi)
- 70-89 : "Bon travail !" (✓ Réussi)
- 50-69 : "Continuez vos efforts !" (⚠ À améliorer)
- 0-49 : "Besoin de révision" (✗ Non réussi)

**COHÉRENCE :** Si tu détectes une erreur de compilation critique, le score ne peut PAS dépasser 60/100.

=== ANCRAGE DU SCORE (bandes impératives) ===
Un même code doit toujours recevoir le même score. Détermine d'abord la bande à partir de ces critères objectifs, puis affine à l'intérieur de la bande avec la grille de points :
- 90-100 : le code compile, tout ce que les TODO demandent est écrit, toutes les notions demandées sont présentes et fonctionnelles.
- 70-89 : le code compile, tout ce que les TODO demandent est écrit, les notions demandées sont présentes ; défauts mineurs seulement.
- 40-69 : vrai travail fourni mais le code ne compile pas (max 60), OU le code compile mais la logique est incomplète ou partiellement incorrecte.
- 20-39 : plusieurs TODO sans le code qu'ils demandent ou plusieurs notions demandées absentes.
- 0-19 : code vide, quasi identique au code de départ, ou sans rapport avec l'énoncé.
Si une section « FAITS VÉRIFIÉS PAR LE SERVEUR » est fournie, ces faits sont exacts : ne les contredis jamais.

FORMATAGE MARKDOWN OBLIGATOIRE :
Formate impérativement tes réponses textuelles (logicAnalysis, whatWorked, keyIssues, etc.) en utilisant la syntaxe Markdown pour une meilleure lisibilité :
- Utilise **gras** pour les mots-clés techniques importants
- Utilise \`code\` pour les noms de variables/méthodes
- Utilise des listes à puces (-) pour énumérer des points
- Structure tes explications en paragraphes séparés

PHILOSOPHIE : Sois un mentor encourageant qui guide vers la réussite, pas un juge qui sanctionne. L'objectif est que l'étudiant comprenne ses erreurs et sache comment s'améliorer.`,

  PEDAGOGICAL_CHATBOT: `Tu es « Coach de code », le coach pédagogique spécialisé en Unity et C# qui aide les étudiants pendant leurs exercices. Parle de toi à la première personne (« je »), jamais à la troisième : pas de « le Coach est fier de toi », mais « je suis fier de toi ». Tu peux dire « le Coach » seulement pour te présenter.

CONTEXTE DE L'EXERCICE :
Titre: {exerciseTitle}
Description: {exerciseDescription}
Concepts: {concepts}

CONTEXTE ACTUEL :
{contextualInfo}

MISSION :
- Réponds aux questions de l'étudiant de manière pédagogique
- Adapte tes explications au niveau de difficulté de l'étudiant
- Donne des exemples concrets et pratiques
- Encourage l'apprentissage autonome
- Référence le contexte de l'exercice quand c'est pertinent

STYLE DE RÉPONSE :
- Utilise un ton amical et encourageant
- Explique les concepts étape par étape
- Donne des exemples de code quand nécessaire
- Propose des ressources complémentaires
- Pose des questions pour vérifier la compréhension
- FORMATE tes réponses en Markdown pour une meilleure lisibilité :
  * Utilise **gras** pour les mots-clés importants
  * Utilise \`code\` pour les noms de variables/méthodes
  * Utilise des listes à puces pour énumérer des points
  * Utilise > pour les citations importantes
  * Utilise des paragraphes séparés pour structurer tes explications

JARGON — RÈGLE ABSOLUE (public débutant) :
- À la PREMIÈRE occurrence de chaque terme technique, ajoute immédiatement une mini-explication en langage courant entre parenthèses : « une variable (une boîte avec un nom où ranger une valeur) », « une méthode (un bloc d'actions que tu peux déclencher par son nom) », « un GameObject (un objet posé dans ta scène de jeu) »
- Ne présume JAMAIS que l'étudiant connaît le vocabulaire de la programmation, même les mots qui te semblent basiques (fonction, paramètre, boucle, compiler...)
- Ces mini-explications ne comptent pas comme des détours : ne raccourcis jamais tes explications pour les compenser

CONTRAINTES :
- Ne donne jamais la solution complète directement
- Guide l'étudiant vers la découverte
- Reste focalisé sur l'apprentissage
- Adapte la complexité à l'étudiant
- Encourage l'expérimentation

CONTRAINTES DE LONGUEUR - TRÈS IMPORTANT :
- Tes réponses doivent être **courtes et directes** (maximum 3-4 phrases par paragraphe)
- Si une explication nécessite plus de détails, **divise-la en plusieurs messages plus courts**
- Utilise des listes à puces pour structurer les points clés
- Évite les longs blocs de texte - privilégie des réponses concises et actionables
- Focus sur l'essentiel : réponds précisément à la question posée sans détours

Question de l'étudiant: {userQuestion}`
} as const;

// === B4 : mode « explication guidée » du chatbot (dernier recours) ===
// Bloc ajouté au prompt du chatbot quand l'étudiant active le mode guidé.
// Commun aux 3 modes d'apprentissage : le template du mode fournit déjà le langage, le public et le ton.
export const GUIDED_MODE_CHAT_INSTRUCTIONS = {
  fr: `

MODE GUIDÉ ACTIVÉ — L'étudiant est complètement bloqué et a demandé à être pris par la main. Ces règles REMPLACENT le style de réponse habituel :
- Donne UNE SEULE instruction à la fois, jamais deux. Une fois l'instruction donnée, termine ton message.
- L'instruction doit être concrète et localisée dans SON code actuel (fourni dans le contexte) : cite le numéro de ligne ou le commentaire TODO exact où écrire (exemple : « Ligne 12, juste sous le commentaire TODO, écris la déclaration de la variable : commence par le type, puis son nom »).
- Tu désignes l'endroit précis dans l'éditeur, mais c'est l'étudiant qui écrit : indique la forme de ce qu'il faut écrire, PAS la solution complète de l'exercice, même s'il la réclame.
- À chaque nouveau message, commence par vérifier dans son code actuel si l'étape précédente est faite et correcte. Si oui, félicite en une phrase puis donne l'étape suivante. Si non, réexplique la MÊME étape autrement, plus simplement — ne passe pas à la suite.
- N'annonce jamais plusieurs étapes d'avance.
- Termine chaque message en demandant à l'étudiant de te dire quand c'est fait (ou ce qu'il voit s'il est bloqué).`,
  en: `

GUIDED MODE ENABLED — The student is completely stuck and asked to be taken by the hand. These rules REPLACE the usual response style:
- Give ONE single instruction at a time, never two. Once the instruction is given, end your message.
- The instruction must be concrete and located in THEIR current code (provided in the context): point to the exact line number or TODO comment where to write (example: "Line 12, right under the TODO comment, write the variable declaration: start with the type, then its name").
- You point to the precise spot in the editor, but the student does the writing: describe the shape of what to write, NOT the complete solution of the exercise, even if they ask for it.
- At each new message, first check in their current code whether the previous step was done correctly. If yes, congratulate in one sentence then give the next step. If not, re-explain the SAME step differently, more simply — do not move on.
- Never announce several steps ahead.
- End every message by asking the student to tell you when it's done (or what they see if they're stuck).`
} as const;

// === Instructions spécifiques selon la charge cognitive ===
export const COGNITIVE_LOAD_INSTRUCTIONS = {
  [CognitiveLoad.ISOLATED]: `
INSTRUCTIONS SPÉCIALES POUR CHARGE COGNITIVE FAIBLE :
- Énoncé COURT et DIRECT (maximum 3-4 phrases)
- Une seule tâche précise à accomplir
- Instructions ultra-concises : "Fais X pour obtenir Y"
- Évite les explications longues ou le contexte complexe
- Focus sur l'action immédiate à réaliser
- Exemple d'énoncé : "Modifie la vitesse du joueur quand la touche Shift est pressée. Utilise Input.GetKey() et change la variable speed."`,

  [CognitiveLoad.COMBINED]: `
INSTRUCTIONS SPÉCIALES POUR CHARGE COGNITIVE MODÉRÉE :
- Énoncé de longueur standard (5-8 phrases)
- 2-3 tâches liées à accomplir
- Instructions claires avec un peu de contexte
- Explications brèves des concepts utilisés
- Structure en étapes numérotées
- Exemple d'énoncé : "Crée un système de collecte. 1) Détecte la collision avec un item. 2) Augmente le score. 3) Détruis l'objet collecté."`,

  [CognitiveLoad.INTEGRATED]: `
INSTRUCTIONS SPÉCIALES POUR CHARGE COGNITIVE ÉLEVÉE :
- Énoncé détaillé avec contexte complet
- Multiples tâches interconnectées
- Explications approfondies des concepts
- Contexte Unity et architectural fourni
- Considérations sur l'intégration et les bonnes pratiques
- Exemple d'énoncé : "Implémente un système de gestion d'inventaire avec UI. Considère la persistance des données, l'interaction utilisateur, et l'architecture MVC."`
} as const;

export const PREDEFINED_TOPICS: PredefinedTopic[] = [
  {
    name: 'arrays',
    displayName: 'Tableaux',
    icon: '📚',
    concepts: [ConceptCategory.DATA_TYPES, ConceptCategory.VARIABLES],
    depths: {
      basic: {
        displayName: 'Les bases',
        description: 'Déclarer, initialiser et accéder aux éléments'
      },
      advanced: {
        displayName: 'Manipulations avancées',
        description: 'Trier, chercher, redimensionner avec Array.Sort(), Array.Find()'
      }
    }
  },
  {
    name: 'physics',
    displayName: 'Physique',
    icon: '⚽',
    concepts: [ConceptCategory.PHYSICS_RIGIDBODY, ConceptCategory.COMPONENT_ACCESS],
    depths: {
      basic: {
        displayName: 'Mouvement simple',
        description: 'Modifier la vélocité pour créer des mouvements'
      },
      advanced: {
        displayName: 'Forces et collisions',
        description: 'AddForce, types de collisions, PhysicMaterial'
      }
    }
  },
  {
    name: 'input',
    displayName: 'Gestion d\'Input',
    icon: '🎮',
    concepts: [ConceptCategory.INPUT_HANDLING, ConceptCategory.CONDITIONALS],
    depths: {
      basic: {
        displayName: 'Entrées clavier/souris',
        description: 'Input.GetKey, Input.GetKeyDown, Input.GetButton'
      },
      advanced: {
        displayName: 'Système d\'input avancé',
        description: 'Input.GetAxis, Input Manager, événements input'
      }
    }
  },
  {
    name: 'gameobjects',
    displayName: 'GameObjects',
    icon: '🎯',
    concepts: [ConceptCategory.GAMEOBJECT_MANIPULATION, ConceptCategory.COMPONENT_ACCESS],
    depths: {
      basic: {
        displayName: 'Manipulation basique',
        description: 'Find, Instantiate, Destroy, tag et name'
      },
      advanced: {
        displayName: 'Hiérarchie et composants',
        description: 'Parent/enfant, GetComponent, AddComponent'
      }
    }
  },
  {
    name: 'variables',
    displayName: 'Variables',
    icon: '📦',
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES, ConceptCategory.ACCESS_MODIFIERS],
    depths: {
      basic: {
        displayName: 'Types et déclaration',
        description: 'int, float, string, bool, déclaration et assignation'
      },
      advanced: {
        displayName: 'Modificateurs et sérialisation',
        description: 'public, private, [SerializeField], static'
      }
    }
  },
  {
    name: 'methods',
    displayName: 'Méthodes',
    icon: '⚙️',
    concepts: [ConceptCategory.METHODS, ConceptCategory.ACCESS_MODIFIERS],
    depths: {
      basic: {
        displayName: 'Déclaration et appel',
        description: 'void, return, paramètres simples'
      },
      advanced: {
        displayName: 'Signatures complexes',
        description: 'Surcharge, paramètres optionnels, out/ref'
      }
    }
  },
  {
    name: 'conditionals',
    displayName: 'Conditions',
    icon: '🔀',
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    depths: {
      basic: {
        displayName: 'if/else simples',
        description: 'Conditions basiques avec opérateurs de comparaison'
      },
      advanced: {
        displayName: 'Logique complexe',
        description: 'Switch, opérateurs logiques, conditions imbriquées'
      }
    }
  },
  {
    name: 'loops',
    displayName: 'Boucles',
    icon: '🔄',
    concepts: [ConceptCategory.LOOPS, ConceptCategory.DATA_TYPES],
    depths: {
      basic: {
        displayName: 'for et foreach',
        description: 'Boucles simples avec collections'
      },
      advanced: {
        displayName: 'Boucles imbriquées',
        description: 'while, do-while, break, continue, boucles complexes'
      }
    }
  }
];

export const DEFAULT_DIFFICULTY_SETTINGS: Omit<DifficultySettings, 'userId' | 'updatedAt'> = {
  // Use the absolute minimums for a brand-new user
  algorithmicComplexity: AlgorithmicComplexity.SEQUENTIAL, // minimum
  unityApiKnowledge: UnityApiKnowledge.FUNDAMENTAL,        // minimum
  codeScope: CodeScope.ONE_LINE,                           // minimum
  cognitiveLoad: CognitiveLoad.ISOLATED                    // minimum
};

export const DIFFICULTY_AXIS_LABELS: DifficultyLabels = {
  algorithmicComplexity: {
    [AlgorithmicComplexity.SEQUENTIAL]: 'Séquentiel',
    [AlgorithmicComplexity.BASIC]: 'Basique',
    [AlgorithmicComplexity.INTERMEDIATE]: 'Intermédiaire',
    [AlgorithmicComplexity.ADVANCED]: 'Avancé'
  },
  unityApiKnowledge: {
    [UnityApiKnowledge.FUNDAMENTAL]: 'Fondamental',
    [UnityApiKnowledge.COMMON]: 'Courant',
    [UnityApiKnowledge.SPECIFIC]: 'Spécifique'
  },
  codeScope: {
    [CodeScope.ONE_LINE]: 'Une Ligne',
    [CodeScope.FUNCTION]: 'Une Fonction',
    [CodeScope.COMPONENT]: 'Un Composant',
    [CodeScope.INTERACTION]: 'Interaction'
  },
  cognitiveLoad: {
    [CognitiveLoad.ISOLATED]: 'Un Seul',
    [CognitiveLoad.COMBINED]: 'Quelques-uns',
    [CognitiveLoad.INTEGRATED]: 'Plusieurs'
  }
};

// === Python/JS Mode ===

export const PYJS_CONCEPT_GROUPS = {
  'Fondamentaux Programmation': [
    ConceptCategory.DATA_TYPES,
    ConceptCategory.VARIABLES,
    ConceptCategory.OPERATORS,
    ConceptCategory.CONDITIONALS,
    ConceptCategory.LOOPS,
    ConceptCategory.METHODS,
    ConceptCategory.COMMENTS,
    ConceptCategory.ERROR_IDENTIFICATION,
    ConceptCategory.NAMING_CONVENTIONS
  ],
  'Concepts Python/JS': [
    ConceptCategory.PYJS_PRINT_OUTPUT,
    ConceptCategory.PYJS_USER_INPUT,
    ConceptCategory.PYJS_STRINGS,
    ConceptCategory.PYJS_LISTS_ARRAYS,
    ConceptCategory.PYJS_DICTIONARIES,
    ConceptCategory.PYJS_TYPE_CONVERSION
  ]
} as const;

export const PYJS_CONCEPT_LABELS: Partial<Record<ConceptCategory, string>> = {
  [ConceptCategory.DATA_TYPES]: 'Types de données (int, str, float, bool, list)',
  [ConceptCategory.VARIABLES]: 'Déclaration et assignation de variables',
  [ConceptCategory.METHODS]: 'Fonctions (def / function)',
  [ConceptCategory.NAMING_CONVENTIONS]: 'Conventions de nommage (snake_case / camelCase)',
  [ConceptCategory.PYJS_PRINT_OUTPUT]: 'Affichage (print / console.log)',
  [ConceptCategory.PYJS_USER_INPUT]: 'Saisie utilisateur (input / prompt)',
  [ConceptCategory.PYJS_STRINGS]: 'Manipulation de chaînes de caractères',
  [ConceptCategory.PYJS_LISTS_ARRAYS]: 'Listes et tableaux',
  [ConceptCategory.PYJS_DICTIONARIES]: 'Dictionnaires / Objets',
  [ConceptCategory.PYJS_TYPE_CONVERSION]: 'Conversion de types (int(), str() / parseInt(), String())'
};

export const PYTHON_JS_PREDEFINED_TOPICS: PredefinedTopic[] = [
  {
    name: 'variables_basics',
    displayName: 'Variables',
    icon: '📦',
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES],
    depths: {
      basic: {
        displayName: 'Les bases',
        description: 'Déclarer et utiliser des variables simples'
      },
      advanced: {
        displayName: 'Types et conversions',
        description: 'Types complexes, conversion entre types'
      }
    }
  },
  {
    name: 'print_output',
    displayName: 'Affichage',
    icon: '🖥️',
    concepts: [ConceptCategory.PYJS_PRINT_OUTPUT, ConceptCategory.PYJS_STRINGS],
    depths: {
      basic: {
        displayName: 'print / console.log',
        description: 'Afficher du texte et des variables'
      },
      advanced: {
        displayName: 'Formatage avancé',
        description: 'f-strings, template literals, concaténation'
      }
    }
  },
  {
    name: 'conditionals',
    displayName: 'Conditions',
    icon: '🔀',
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    depths: {
      basic: {
        displayName: 'if/else simples',
        description: 'Conditions basiques avec opérateurs de comparaison'
      },
      advanced: {
        displayName: 'Logique complexe',
        description: 'elif/else if, opérateurs logiques, conditions imbriquées'
      }
    }
  },
  {
    name: 'loops',
    displayName: 'Boucles',
    icon: '🔄',
    concepts: [ConceptCategory.LOOPS, ConceptCategory.PYJS_LISTS_ARRAYS],
    depths: {
      basic: {
        displayName: 'for et while',
        description: 'Boucles simples avec compteur ou condition'
      },
      advanced: {
        displayName: 'Parcours de collections',
        description: 'Boucles sur listes, break, continue, boucles imbriquées'
      }
    }
  },
  {
    name: 'functions',
    displayName: 'Fonctions',
    icon: '⚙️',
    concepts: [ConceptCategory.METHODS],
    depths: {
      basic: {
        displayName: 'Déclaration et appel',
        description: 'Définir et appeler des fonctions simples'
      },
      advanced: {
        displayName: 'Paramètres et retours',
        description: 'Paramètres, valeurs de retour, portée des variables'
      }
    }
  },
  {
    name: 'strings',
    displayName: 'Chaînes de caractères',
    icon: '📝',
    concepts: [ConceptCategory.PYJS_STRINGS, ConceptCategory.DATA_TYPES],
    depths: {
      basic: {
        displayName: 'Les bases',
        description: 'Créer, concaténer et accéder aux caractères'
      },
      advanced: {
        displayName: 'Méthodes de chaînes',
        description: 'split, join, replace, upper/lower, slicing'
      }
    }
  },
  {
    name: 'lists',
    displayName: 'Listes / Tableaux',
    icon: '📚',
    concepts: [ConceptCategory.PYJS_LISTS_ARRAYS, ConceptCategory.LOOPS],
    depths: {
      basic: {
        displayName: 'Créer et accéder',
        description: 'Déclarer, initialiser et accéder aux éléments'
      },
      advanced: {
        displayName: 'Manipulations',
        description: 'Ajouter, supprimer, trier, rechercher'
      }
    }
  },
  {
    name: 'dictionaries',
    displayName: 'Dictionnaires',
    icon: '🗂️',
    concepts: [ConceptCategory.PYJS_DICTIONARIES, ConceptCategory.PYJS_LISTS_ARRAYS],
    depths: {
      basic: {
        displayName: 'Clé-valeur',
        description: 'Créer et accéder aux paires clé-valeur'
      },
      advanced: {
        displayName: 'Parcours et méthodes',
        description: 'Itérer, modifier, méthodes courantes'
      }
    }
  }
];

// === Godot Mode ===

export const GODOT_CONCEPT_GROUPS = {
  'Fondamentaux GDScript': [
    ConceptCategory.DATA_TYPES,
    ConceptCategory.VARIABLES,
    ConceptCategory.OPERATORS,
    ConceptCategory.CONDITIONALS,
    ConceptCategory.LOOPS,
    ConceptCategory.METHODS,
    ConceptCategory.COMMENTS,
    ConceptCategory.ERROR_IDENTIFICATION,
    ConceptCategory.NAMING_CONVENTIONS,
    ConceptCategory.GODOT_PRINT_OUTPUT,
    ConceptCategory.GODOT_ARRAYS_DICTS
  ],
  'API Godot & Scripting': [
    ConceptCategory.GODOT_NODES_SCENES,
    ConceptCategory.GODOT_LIFECYCLE,
    ConceptCategory.GODOT_SIGNALS,
    ConceptCategory.GODOT_AWAIT_CALLABLES,
    ConceptCategory.GODOT_TYPED_GDSCRIPT
  ],
  'Gamedev & Patterns': [
    ConceptCategory.GODOT_VECTOR_MATH,
    ConceptCategory.GODOT_SCENE_INSTANCING,
    ConceptCategory.GODOT_CHARACTER_MOVEMENT,
    ConceptCategory.GODOT_OOP_PATTERNS
  ]
} as const;

export const GODOT_CONCEPT_LABELS: Partial<Record<ConceptCategory, string>> = {
  [ConceptCategory.DATA_TYPES]: 'Types de données (int, float, String, bool, Array)',
  [ConceptCategory.VARIABLES]: 'Déclaration et assignation de variables (var, const)',
  [ConceptCategory.METHODS]: 'Fonctions (func)',
  [ConceptCategory.NAMING_CONVENTIONS]: 'Conventions de nommage (snake_case)',
  [ConceptCategory.GODOT_PRINT_OUTPUT]: 'Affichage console Godot (print)',
  [ConceptCategory.GODOT_ARRAYS_DICTS]: 'Tableaux (Array) et Dictionnaires GDScript',
  [ConceptCategory.GODOT_NODES_SCENES]: 'Nodes et Scènes (get_node, $, @onready, @export)',
  [ConceptCategory.GODOT_LIFECYCLE]: 'Cycle de vie Godot (_ready, _process, delta)',
  [ConceptCategory.GODOT_SIGNALS]: 'Signaux (signal, emit, connect)',
  [ConceptCategory.GODOT_AWAIT_CALLABLES]: 'Await, Callable et callbacks',
  [ConceptCategory.GODOT_TYPED_GDSCRIPT]: 'GDScript typé et mots-clés avancés (class_name, match, enum)',
  [ConceptCategory.GODOT_VECTOR_MATH]: 'Maths vectorielles (Vector2, normalized, lerp)',
  [ConceptCategory.GODOT_SCENE_INSTANCING]: 'Instanciation de scènes (preload, instantiate, add_child)',
  [ConceptCategory.GODOT_CHARACTER_MOVEMENT]: 'Mouvement de personnage (CharacterBody2D, velocity, move_and_slide)',
  [ConceptCategory.GODOT_OOP_PATTERNS]: 'POO et patterns GDScript (héritage, set/get, Resources, state machines)'
};

export const GODOT_PREDEFINED_TOPICS: PredefinedTopic[] = [
  {
    name: 'variables_basics',
    displayName: 'Variables',
    icon: '📦',
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES],
    depths: {
      basic: {
        displayName: 'Les bases',
        description: 'Déclarer et utiliser des variables simples (var, const)'
      },
      advanced: {
        displayName: 'Typage statique',
        description: 'Types explicites (var pv: int), inférence (:=), conversions'
      }
    }
  },
  {
    name: 'print_output',
    displayName: 'Affichage',
    icon: '🖥️',
    concepts: [ConceptCategory.GODOT_PRINT_OUTPUT, ConceptCategory.VARIABLES],
    depths: {
      basic: {
        displayName: 'print()',
        description: 'Afficher du texte et des variables dans la console Godot'
      },
      advanced: {
        displayName: 'Formatage avancé',
        description: 'str(), concaténation, formatage avec % et format()'
      }
    }
  },
  {
    name: 'conditionals',
    displayName: 'Conditions',
    icon: '🔀',
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    depths: {
      basic: {
        displayName: 'if/else simples',
        description: 'Conditions basiques avec opérateurs de comparaison'
      },
      advanced: {
        displayName: 'Logique complexe',
        description: 'elif, match, opérateurs logiques, conditions imbriquées'
      }
    }
  },
  {
    name: 'loops',
    displayName: 'Boucles',
    icon: '🔄',
    concepts: [ConceptCategory.LOOPS, ConceptCategory.GODOT_ARRAYS_DICTS],
    depths: {
      basic: {
        displayName: 'for et while',
        description: 'Boucles simples avec range() ou condition'
      },
      advanced: {
        displayName: 'Parcours de collections',
        description: 'Boucles sur Array et Dictionary, break, continue'
      }
    }
  },
  {
    name: 'functions',
    displayName: 'Fonctions',
    icon: '⚙️',
    concepts: [ConceptCategory.METHODS],
    depths: {
      basic: {
        displayName: 'Déclaration et appel',
        description: 'Définir (func) et appeler des fonctions simples'
      },
      advanced: {
        displayName: 'Paramètres et retours',
        description: 'Paramètres typés, valeurs de retour (-> int), valeurs par défaut'
      }
    }
  },
  {
    name: 'arrays_dicts',
    displayName: 'Array & Dictionary',
    icon: '📚',
    concepts: [ConceptCategory.GODOT_ARRAYS_DICTS, ConceptCategory.LOOPS],
    depths: {
      basic: {
        displayName: 'Créer et accéder',
        description: 'Déclarer, initialiser et accéder aux éléments'
      },
      advanced: {
        displayName: 'Manipulations',
        description: 'append, erase, size, in, dictionnaires imbriqués'
      }
    }
  },
  {
    name: 'nodes_lifecycle',
    displayName: 'Nodes & Cycle de vie',
    icon: '🌳',
    concepts: [ConceptCategory.GODOT_NODES_SCENES, ConceptCategory.GODOT_LIFECYCLE],
    depths: {
      basic: {
        displayName: 'extends, _ready, $',
        description: 'Attacher un script, accéder aux nodes de la scène'
      },
      advanced: {
        displayName: '_process, @onready, @export',
        description: 'Mouvement avec delta, annotations, queue_free()'
      }
    }
  },
  {
    name: 'signals_callbacks',
    displayName: 'Signaux & Callbacks',
    icon: '📡',
    concepts: [ConceptCategory.GODOT_SIGNALS, ConceptCategory.GODOT_AWAIT_CALLABLES],
    depths: {
      basic: {
        displayName: 'signal, emit, connect',
        description: 'Déclarer, émettre et connecter des signaux simples'
      },
      advanced: {
        displayName: 'Paramètres, Callable, lambdas',
        description: 'Signaux avec arguments, fonctions comme valeurs, lambdas'
      }
    }
  },
  {
    name: 'await_async',
    displayName: 'Await & Asynchrone',
    icon: '⏳',
    concepts: [ConceptCategory.GODOT_AWAIT_CALLABLES, ConceptCategory.GODOT_TYPED_GDSCRIPT],
    depths: {
      basic: {
        displayName: 'await simple',
        description: 'Attendre un signal ou un timer (await get_tree().create_timer)'
      },
      advanced: {
        displayName: 'Coroutines et enchaînements',
        description: 'Enchaîner des await, coroutines, match et enums d\'état'
      }
    }
  },
  {
    name: 'vector_math',
    displayName: 'Vecteurs (Vector2)',
    icon: '🧭',
    concepts: [ConceptCategory.GODOT_VECTOR_MATH],
    depths: {
      basic: {
        displayName: 'Vector2 de base',
        description: 'Créer des Vector2, additionner, multiplier par la vitesse et delta'
      },
      advanced: {
        displayName: 'Distances et interpolation',
        description: 'normalized(), distance_to(), direction_to(), lerp, move_toward'
      }
    }
  },
  {
    name: 'scene_instancing',
    displayName: 'Instancier des scènes',
    icon: '🏭',
    concepts: [ConceptCategory.GODOT_SCENE_INSTANCING],
    depths: {
      basic: {
        displayName: 'preload + instantiate',
        description: 'Charger une scène, créer une instance et l\'ajouter avec add_child()'
      },
      advanced: {
        displayName: 'Spawners et signaux',
        description: 'Spawner en boucle, configurer les instances, connecter leurs signaux, queue_free'
      }
    }
  },
  {
    name: 'character_movement',
    displayName: 'Mouvement du personnage',
    icon: '🕹️',
    concepts: [ConceptCategory.GODOT_CHARACTER_MOVEMENT, ConceptCategory.GODOT_VECTOR_MATH],
    depths: {
      basic: {
        displayName: 'velocity et move_and_slide',
        description: 'Déplacer un CharacterBody2D en top-down avec velocity et move_and_slide()'
      },
      advanced: {
        displayName: 'Gravité, saut, friction',
        description: 'Plateforme : gravité, saut avec is_on_floor(), accélération et move_toward'
      }
    }
  },
  {
    name: 'oop_patterns',
    displayName: 'POO & Patterns',
    icon: '🏗️',
    concepts: [ConceptCategory.GODOT_OOP_PATTERNS],
    depths: {
      basic: {
        displayName: 'Héritage et super',
        description: 'Étendre une classe custom, redéfinir des méthodes, appeler super'
      },
      advanced: {
        displayName: 'set/get, Resources, états',
        description: 'Setters/getters, Resources custom, static, state machines, Tween'
      }
    }
  }
];

// === Mode-aware helper functions ===

export function getConceptGroupsForMode(mode: LearningMode) {
  switch (mode) {
    case LearningMode.PYTHON_JS:
      return PYJS_CONCEPT_GROUPS;
    case LearningMode.GODOT:
      return GODOT_CONCEPT_GROUPS;
    case LearningMode.UNITY_CSHARP:
    default:
      return CONCEPT_GROUPS;
  }
}

export function getConceptLabelsForMode(mode: LearningMode): Partial<Record<ConceptCategory, string>> {
  switch (mode) {
    case LearningMode.PYTHON_JS:
      return { ...CONCEPT_LABELS, ...PYJS_CONCEPT_LABELS };
    case LearningMode.GODOT:
      return { ...CONCEPT_LABELS, ...GODOT_CONCEPT_LABELS };
    case LearningMode.UNITY_CSHARP:
    default:
      return CONCEPT_LABELS;
  }
}

export function getPredefinedTopicsForMode(mode: LearningMode): PredefinedTopic[] {
  switch (mode) {
    case LearningMode.PYTHON_JS:
      return PYTHON_JS_PREDEFINED_TOPICS;
    case LearningMode.GODOT:
      return GODOT_PREDEFINED_TOPICS;
    case LearningMode.UNITY_CSHARP:
    default:
      return PREDEFINED_TOPICS;
  }
} 